import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

// DOMPurify (npm) — CommonJS/webpack interop exposes the module object (with .sanitize) directly.
external interface DomPurify {
    fun sanitize(dirty: String, config: dynamic = definedExternally): String
}

@JsModule("dompurify")
@JsNonModule
external val DOMPurify: DomPurify

/**
 * Allowlist for user-authored CMS HTML (spec 10.1) — also covers the auto-generated match-result
 * Aktualita (match-result Edge Function), which reuses `div`/`span`/`b`/`class` to mirror the
 * `.match-table` treatment from MatchDetailModal.kt (see styles.css). Anything else (script,
 * style, on* …) is stripped.
 */
private val sanitizeConfig: dynamic = js(
    """({
        ALLOWED_TAGS: ['h2','h3','h4','p','br','strong','em','u','s','ul','ol','li','blockquote',
                       'a','img','table','thead','tbody','tr','th','td','div','span','b'],
        ALLOWED_ATTR: ['href','target','rel','src','alt','title','colspan','rowspan','class']
    })"""
)

fun sanitizeHtml(html: String): String = DOMPurify.sanitize(html, sanitizeConfig)

/** Renders sanitized CMS HTML into a container (spec 10.1). Re-sanitizes whenever `html` changes.
 *
 * Also delegates clicks on `.protocol-photo-link` (the match-result Aktualita's protocol-photo
 * thumbnail, spec 10.9) to the shared [ImageLightbox] instead of letting the link navigate — this
 * raw-HTML content can't hold its own Compose click handler, so a native listener on the container
 * does the job. The link's `href`/`target` stay in the markup as a plain-navigation fallback. */
@Composable
fun RichContent(html: String) {
    Div(attrs = { classes("rich-content") }) {
        DisposableRefEffect(html) { element ->
            val el = element as HTMLElement
            el.innerHTML = sanitizeHtml(html)
            // Set before the browser's next rendering opportunity, including CMS-authored images.
            val images = el.querySelectorAll("img")
            for (i in 0 until images.length) {
                val image = images.item(i) as? HTMLElement ?: continue
                image.setAttribute("loading", "lazy")
                image.setAttribute("decoding", "async")
            }
            val listener: (org.w3c.dom.events.Event) -> Unit = { ev ->
                val link = ev.target.asDynamic()?.closest(".protocol-photo-link")
                if (link != null) {
                    ev.preventDefault()
                    (link.getAttribute("href") as? String)?.let { openLightbox(it) }
                }
            }
            el.addEventListener("click", listener)
            onDispose {
                el.removeEventListener("click", listener)
                el.innerHTML = ""
            }
        }
    }
}
